import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AmountCalculation,
  CardRate,
  FXRates,
  RemittanceBetaState,
  RemittancePurpose,
  VendorCode,
} from "../types/remittance";
import { AppConstants } from "../utils";
import { IRemittanceAPIProps } from "../api/IRemittanceAPI";
import { IDocumentsUIProps } from "../types/remittance/documents";
import { IReviewUIProps } from "../types/remittance/review";

const createDocumentSideState = () => ({
  data: null,
  loading: false,
  error: null,
});

const createDocumentUploadState = () => ({
  front: createDocumentSideState(),
  back: createDocumentSideState(),
});

const createDocumentVerificationState = () => ({
  data: null,
  loading: false,
  error: null,
  isVerified: false,
});

const initialState: RemittanceBetaState = {
  general: {},
  progresStep: {
    progresStepData: [...AppConstants.RemittanceProgressStepperData],
    activeStepNumber: 1,
    transactionCancelModal: false,
  },
  modalType: "",
  calculator: {
    purpose: {
      list: [],
      loading: false,
      error: "",
    },
    vendorCode: {
      list: [],
      loading: false,
      error: "",
    },
    cardRate: {
      list: [],
      loading: false,
      error: "",
    },
    cardRateByCurrencyPurpose: "",
    fxRates: {
      data: null,
      loading: false,
      error: "",
    },
    amountCalculation: {
      user_send: 10000,
      base_currency: "INR",
      reciepient_recieve: "",
      lock_reciepient_recieve: false,
      changeInputFrom: "send",
      dest_currency: "USD",
      countryDropdown: { countryName: "United States", currencyName: "USD" },
      purposeCode: "",
      transaction_purpose: "",
      purposeDropdown: null,
      isEducation: false,
      conversion_rate: "",
      vendor_key: "",
      referrerVendor: "",
      transVendorKey: "",
      vendorDropdown: null,
      isBene: true,
      pan_number: "",
      declare_amount: "",
      roundOff: false,
      hop_coins: 0,
      skipApi: false,
    },
    getTransactionSummary: {
      data: null,
      loading: false,
      error: null,
    },
    getPanDeclaration: {
      data: null,
      loading: false,
      error: null,
      pan_number: "",
      declare_amount: "",
    },
    getLockBookRate: {
      data: null,
      loading: false,
      error: null,
    },
  },
  remitter: {
    remitterList: {
      data: null,
      loading: false,
      refreshing: false,
      reload: false,
      error: "",
    },
    selectedRemitter: null,
    resendRemitterKycLink: {
      data: null,
      loading: false,
      loadingType: "",
      error: null,
    },
    deleteRemitter: {
      data: null,
      loading: false,
      error: null,
    },
    getRemitter: {
      data: null,
      loading: false,
      error: null,
    },
    updateRemitter: {
      data: null,
      loading: false,
      error: null,
    },
    updateRemitterInput: null,
    addRemitterInput: {
      email: "",
      mobile: "",
      otp: "",
      isSentOtp: false,
      date: Date.now(),
      resendCount: 0,
    },
    addRemitter: {
      data: null,
      loading: false,
      sendOTPLoading: false,
      validateOTPLoading: false,
      signUpLoading: false,
      sendKycLinkLoading: false,
      resendOTPLoading: false,
      error: null,
    },
    remitterDocuments: {
      ckycStatus: {
        data: null,
        loading: false,
        error: null,
      },
      userDocuments: {
        list: [],
        loading: false,
        error: null,
      },
      documentUploads: {
        PAN_CARD: createDocumentUploadState(),
        AADHAR_CARD: createDocumentUploadState(),
        AADHAR_CARD_BACK: createDocumentUploadState(),
        DRIVING_LICENCE: createDocumentUploadState(),
        PASSPORT_FRONT: createDocumentUploadState(),
        PASSPORT_BACK: createDocumentUploadState(),
        VOTER_CARD: createDocumentUploadState(),
      },
      documentVerification: {
        PAN_CARD: createDocumentVerificationState(),
        AADHAR_CARD: createDocumentVerificationState(),
        AADHAR_CARD_BACK: createDocumentVerificationState(),
        DRIVING_LICENCE: createDocumentVerificationState(),
        PASSPORT_FRONT: createDocumentVerificationState(),
        PASSPORT_BACK: createDocumentVerificationState(),
        VOTER_CARD: createDocumentVerificationState(),
      },
    },
    quoteDetails: {
      list: [],
      loading: false,
      error: null,
    },
    selectedQuoteUniqueKey: null,
    proceedWithoutQuote: false,
    pendingAvanseQuoteFlow: false,
    avanseQuoteModalOpen: false,
  },
  recipient: {
    recipientList: {
      data: null,
      loading: false,
      refreshing: false,
      reload: false,
      error: "",
    },
    selectedRecipient: null,
    deleteRecipient: {
      data: null,
      loading: false,
      error: null,
    },
    recipientDetail: {
      data: {},
      loading: {},
      error: {},
    },
  },
  addrecipient: {
    recipientStepNumber: 0,
    recipientInput: {
      step1: {
        recipientName: "",
        recipientCategoryDropdown: null,
      },
      step2: {
        relationship: "",
        country: "",
        city: "",
        pincode: "",
        addressLine1: "",
        addressLine2: "",
      },
      step3: {
        isSwift: false,
        swiftOrIbanNumber: "",
        swiftIbanLoading: false,
        swiftIbanError: null,
        swiftIbanVerified: false,
        bankAccNo: "",
        confirmBankAccNo: "",
        bankAccCountry: "",
        bankName: "",
        sortBSBTransitRoutingPlaceholder: "Sort/BSB/Transit/Routing(Optional)",
        sortBSBTransitRouting: "",
        referenceId: "",
        bankAddress: "",
        unitNumber: "",
        correspondingAccount: false,
      },
      step4: {
        bankAccNo: "",
        confirmBankAccNo: "",
        bankName: "",
      },
    },
    addRecipientSubmit: {
      data: null,
      loading: false,
      error: null,
    },
    recipientCategoryList: {
      list: [],
      loading: false,
      error: null,
    },
  },
  documents: {
    documentsList: {
      list: [],
      loading: false,
      error: null,
    },
    leadDocumentsList: {
      list: [],
      loading: false,
      error: null,
    },
    recipientLeadDocs: [],
    previewDocs: {
      fileURL: "",
      loading: false,
    },
  },
  review: {
    reviewDetails: {
      lanNumber: "",
      utrNumber: "",
      sourceOfFundsDropdown: null,
      termsAndConditions: false,
      consentForm: false,
      currentIP: "",
    },
    sourceOfFundsData: [...AppConstants.RemittanceSouceOfFunds],
    remitterSignedStatus: {
      data: null,
      loading: false,
      error: null,
    },
    consentData: {
      data: null,
      loading: false,
      error: null,
    },
    viewA2Form: {
      loading: false,
      error: null,
    },
    downloadA2Form: {
      loading: false,
      error: null,
    },
  },
  payment: {
    neftRtgsBankDetails: {
      data: null,
      loading: false,
      error: null,
    },
    shareNeftRtgsAccount: {
      data: null,
      loading: false,
      error: null,
    },
    netBankingList: {
      list: [],
      loading: false,
      error: "",
    },
    sendPaymentLinkInput: {
      bankDropdown: null,
      accountNumber: "",
      IFSCcode: "",
      pennyDropLoading: false,
      requestRazorLoading: false,
    },
  },
  transactionDetail: {
    data: null,
    loading: false,
    error: null,
  },
};

const remittanceSlice = createSlice({
  name: "remittance",
  initialState,
  reducers: {
    // Stepper Index
    setActiveStepNumber(state, action: PayloadAction<number>) {
      state.progresStep.activeStepNumber = action.payload;
    },

    // TRANSACTION CANCEL MODAL
    setTransactionCancelModal(state, action: PayloadAction<boolean>) {
      state.progresStep.transactionCancelModal = action.payload;
    },

    // SET MODAL TYLE
    setModalType(state, action: PayloadAction<string>) {
      state.modalType = action.payload;
    },

    // PURPOSE LIST
    fetchPurposeListRequested(state) {
      state.calculator.purpose.loading = true;
      state.calculator.purpose.error = null;
    },
    fetchPurposeListSucceeded(state, action: PayloadAction<RemittancePurpose[]>) {
      state.calculator.purpose.loading = false;
      state.calculator.purpose.list = action.payload;
    },
    fetchPurposeListFailed(state, action: PayloadAction<string>) {
      state.calculator.purpose.loading = false;
      state.calculator.purpose.error = action.payload;
    },

    // VENDOR CODE LIST
    fetchLinkedVendorCodeRequested(state) {
      state.calculator.vendorCode.loading = true;
      state.calculator.vendorCode.error = null;
    },
    fetchLinkedVendorCodeSucceeded(state, action: PayloadAction<VendorCode[]>) {
      state.calculator.vendorCode.loading = false;
      state.calculator.vendorCode.list = action.payload;
    },
    fetchLinkedVendorCodeFailed(state, action: PayloadAction<string>) {
      state.calculator.vendorCode.loading = false;
      state.calculator.vendorCode.error = action.payload;
    },

    //CARD RATE
    fetchCardRateRequested(state) {
      state.calculator.cardRate.loading = true;
      state.calculator.cardRate.error = null;
    },
    fetchCardRateSucceeded(state, action: PayloadAction<CardRate[]>) {
      state.calculator.cardRate.loading = false;
      state.calculator.cardRate.list = action.payload;
    },
    fetchCardRateFailed(state, action: PayloadAction<string>) {
      state.calculator.cardRate.loading = false;
      state.calculator.cardRate.error = action.payload;
    },

    // SET CARD RATE BY CURRENCY AND PURPOSE
    setCardRateByCurrencyPurpose(state, action: PayloadAction<string>) {
      state.calculator.cardRateByCurrencyPurpose = action.payload;
    },

    // FETCH FX RATES
    fetchFxRatesRequested(state, action: PayloadAction<string>) {
      state.calculator.fxRates.loading = true;
      state.calculator.fxRates.error = null;
    },
    fetchFxRatesSucceeded(state, action: PayloadAction<FXRates>) {
      state.calculator.fxRates.loading = false;
      state.calculator.fxRates.data = action.payload;
    },
    fetchFxRatesFailed(state, action: PayloadAction<string>) {
      state.calculator.fxRates.loading = false;
      state.calculator.fxRates.error = action.payload;
    },

    // AMOUNT CALCULATION
    setAmountCalculation(state, action: PayloadAction<any>) {
      state.calculator.amountCalculation = {
        ...state.calculator.amountCalculation,
        ...action.payload,
      };
    },

    // GET TRANSACTION SUMMARY
    fetchTransactionSummaryRequested(state, action: PayloadAction<any>) {
      state.calculator.getTransactionSummary.loading = true;
      state.calculator.getTransactionSummary.error = null;
    },
    fetchTransactionSummarySucceeded(state, action: PayloadAction<any>) {
      state.calculator.getTransactionSummary.loading = false;
      state.calculator.getTransactionSummary.data = action.payload;
    },
    fetchTransactionSummaryFailed(state, action: PayloadAction<any>) {
      state.calculator.getTransactionSummary.loading = false;
      state.calculator.getTransactionSummary.error = action.payload;
    },
    resetFetchTransactionSummary(state) {
      state.calculator.getTransactionSummary.data = null;
    },

    // FETCH PAN DECLARATION GET TRANSACTION SUMMARY
    fetchPanDeclarationRequested(state, action: PayloadAction<any>) {
      state.calculator.getPanDeclaration.loading = true;
      state.calculator.getPanDeclaration.error = null;
    },
    fetchPanDeclarationSucceeded(state, action: PayloadAction<any>) {
      state.calculator.getPanDeclaration.loading = false;
      state.calculator.getPanDeclaration.data = action.payload;
    },
    fetchPanDeclarationFailed(state, action: PayloadAction<any>) {
      state.calculator.getPanDeclaration.loading = false;
      state.calculator.getPanDeclaration.error = action.payload;
    },
    resetPanDeclaration(state) {
      state.calculator.getPanDeclaration.loading = false;
      state.calculator.getPanDeclaration.error = null;
      state.calculator.getPanDeclaration.data = null;
    },

    // LOCK BOOK RATE
    setLockBookRate(state, action: PayloadAction<any>) {
      state.calculator.getLockBookRate = {
        ...state.calculator.getLockBookRate,
        ...action.payload,
      };
    },

    // PURPOSE LIST
    fetchRemitterListRequested(
      state,
      action: PayloadAction<IRemittanceAPIProps.IRemitterListPayload>
    ) {
      state.remitter.remitterList.loading = true;
      state.remitter.remitterList.error = null;
    },
    fetchRemitterListSucceeded(state, action: PayloadAction<any>) {
      state.remitter.remitterList.loading = false;
      state.remitter.remitterList.data = action.payload;
    },
    fetchRemitterListFailed(state, action: PayloadAction<any>) {
      state.remitter.remitterList.loading = false;
      state.remitter.remitterList.error = action.payload;
    },
    setRemitterListState(state, action: PayloadAction<any>) {
      state.remitter.remitterList = {
        ...state.remitter.remitterList,
        ...action.payload,
      };
    },
    setSelectedRemitter(state, action: PayloadAction<any>) {
      const nextUserKey = action.payload?.userKey;
      const currentUserKey = state.remitter.selectedRemitter?.userKey;

      state.remitter.selectedRemitter = action.payload;

      if (currentUserKey && nextUserKey !== currentUserKey) {
        state.remitter.quoteDetails = {
          list: [],
          loading: false,
          error: null,
        };
        state.remitter.selectedQuoteUniqueKey = null;
        state.remitter.proceedWithoutQuote = false;
      }
    },

    fetchSearchQuoteDetailsRequested(
      state,
      _action: PayloadAction<IRemittanceAPIProps.SearchQuoteDetailsPayload>
    ) {
      state.remitter.quoteDetails.loading = true;
      state.remitter.quoteDetails.error = null;
    },
    fetchSearchQuoteDetailsSucceeded(state, action: PayloadAction<any[]>) {
      state.remitter.quoteDetails.loading = false;
      state.remitter.quoteDetails.list = action.payload;
    },
    fetchSearchQuoteDetailsFailed(state, action: PayloadAction<string>) {
      state.remitter.quoteDetails.loading = false;
      state.remitter.quoteDetails.error = action.payload;
      state.remitter.quoteDetails.list = [];
    },
    setSelectedQuoteUniqueKey(state, action: PayloadAction<string | null>) {
      state.remitter.selectedQuoteUniqueKey = action.payload;
      if (action.payload) {
        state.remitter.proceedWithoutQuote = false;
      }
    },
    setProceedWithoutQuote(state, action: PayloadAction<boolean>) {
      state.remitter.proceedWithoutQuote = action.payload;
      if (action.payload) {
        state.remitter.selectedQuoteUniqueKey = null;
      }
    },
    setPendingAvanseQuoteFlow(state, action: PayloadAction<boolean>) {
      state.remitter.pendingAvanseQuoteFlow = action.payload;
      if (!action.payload) {
        state.remitter.avanseQuoteModalOpen = false;
      }
    },
    setAvanseQuoteModalOpen(state, action: PayloadAction<boolean>) {
      state.remitter.avanseQuoteModalOpen = action.payload;
    },

    // RESEND REMITTER KYC LINK
    setResendRemitterKycLink(state, action: PayloadAction<any>) {
      state.remitter.resendRemitterKycLink = {
        ...state.remitter.resendRemitterKycLink,
        ...action.payload,
      };
    },

    // DELETE REMITTER
    setDeleteRemitter(state, action: PayloadAction<any>) {
      state.remitter.deleteRemitter = {
        ...state.remitter.deleteRemitter,
        ...action.payload,
      };
    },

    // GET REMITTER
    setGetRemitter(state, action: PayloadAction<any>) {
      state.remitter.getRemitter = {
        ...state.remitter.getRemitter,
        ...action.payload,
      };
    },

    // UPDATE REMITTER
    setUpdateRemitter(state, action: PayloadAction<any>) {
      state.remitter.updateRemitter = {
        ...state.remitter.updateRemitter,
        ...action.payload,
      };
    },

    // UPDATE REMITTER INPUT
    setUpdateRemitterInput(state, action: PayloadAction<any>) {
      state.remitter.updateRemitterInput = {
        ...state.remitter.updateRemitterInput,
        ...action.payload,
      };
    },

    // ADD REMITTER INPUT
    setAddRemitterInput(state, action: PayloadAction<any>) {
      state.remitter.addRemitterInput = {
        ...state.remitter.addRemitterInput,
        ...action.payload,
      };
    },

    // ADD REMITTER RESPONSE
    setAddRemitterResponse(state, action: PayloadAction<any>) {
      state.remitter.addRemitter = {
        ...state.remitter.addRemitter,
        ...action.payload,
      };
    },

    // SET REMITTER DOCUMENTS SECTION
    setRemitterDocumentsSection(state, action: PayloadAction<any>) {
      state.remitter.remitterDocuments = {
        ...state.remitter.remitterDocuments,
        ...action.payload,
      };
    },

    // SET DOCUMENT UPLOAD STATE
    setDocumentUploadState(
      state,
      action: PayloadAction<{
        docType: string;
        side: "front" | "back";
        data?: any;
        loading?: boolean;
        error?: string | null;
      }>
    ) {
      const { docType, side, data, loading, error } = action.payload;
      if (!state.remitter.remitterDocuments.documentUploads[docType]) {
        state.remitter.remitterDocuments.documentUploads[docType] = createDocumentUploadState();
      }

      if (data !== undefined) {
        state.remitter.remitterDocuments.documentUploads[docType][side].data = data;
      }

      if (loading !== undefined) {
        state.remitter.remitterDocuments.documentUploads[docType][side].loading = loading;
      }

      if (error !== undefined) {
        state.remitter.remitterDocuments.documentUploads[docType][side].error = error;
      }
    },

    // SET DOCUMENT VERIFICATION STATE
    setDocumentVerificationState(
      state,
      action: PayloadAction<{
        docType: string;
        data?: any;
        loading?: boolean;
        error?: string | null;
        isVerified?: boolean;
      }>
    ) {
      const { docType, data, loading, error, isVerified } = action.payload;

      if (!state.remitter.remitterDocuments.documentVerification[docType]) {
        state.remitter.remitterDocuments.documentVerification[docType] =
          createDocumentVerificationState();
      }

      if (data !== undefined) {
        state.remitter.remitterDocuments.documentVerification[docType].data = data;
      }

      if (loading !== undefined) {
        state.remitter.remitterDocuments.documentVerification[docType].loading = loading;
      }

      if (error !== undefined) {
        state.remitter.remitterDocuments.documentVerification[docType].error = error;
      }

      if (isVerified !== undefined) {
        state.remitter.remitterDocuments.documentVerification[docType].isVerified = isVerified;
      }
    },

    // RECIPIENT LIST
    fetchRecipientListRequested(
      state,
      action: PayloadAction<IRemittanceAPIProps.IRecipientListPayload>
    ) {
      state.recipient.recipientList.loading = true;
      state.recipient.recipientList.error = null;
    },
    fetchRecipientListSucceeded(state, action: PayloadAction<any>) {
      state.recipient.recipientList.loading = false;
      state.recipient.recipientList.data = action.payload;
    },
    fetchRecipientListFailed(state, action: PayloadAction<any>) {
      state.recipient.recipientList.loading = false;
      state.recipient.recipientList.error = action.payload;
    },
    setSelectedRecipient(state, action: PayloadAction<any>) {
      state.recipient.selectedRecipient = action.payload;
    },
    setRecipientListState(state, action: PayloadAction<any>) {
      state.recipient.recipientList = {
        ...state.recipient.recipientList,
        ...action.payload,
      };
    },

    // DELETE RECIPIENT
    setDeleteRecipient(state, action: PayloadAction<any>) {
      state.recipient.deleteRecipient = {
        ...state.recipient.deleteRecipient,
        ...action.payload,
      };
    },

    // FETCH RECIPIENT DETAIL
    setRecipientDetailState(state, action) {
      state.recipient.recipientDetail = {
        ...state.recipient.recipientDetail,
        ...action.payload,
      };
    },

    // DOCUMENTS LIST
    fetchDocumentsListRequested(
      state,
      action: PayloadAction<IRemittanceAPIProps.IDocumentsListPayload>
    ) {
      state.documents.documentsList.loading = true;
      state.documents.documentsList.error = null;
      state.documents.documentsList.list = [];
    },
    fetchDocumentsListSucceeded(state, action: PayloadAction<IDocumentsUIProps.DocumentsList[]>) {
      state.documents.documentsList.loading = false;
      state.documents.documentsList.list = action.payload;
    },
    fetchDocumentsListFailed(state, action: PayloadAction<any>) {
      state.documents.documentsList.loading = false;
      state.documents.documentsList.error = action.payload;
      state.documents.documentsList.list = [];
    },

    // LEAD DOCUMENTS LIST
    fetchLeadDocumentsListRequested(
      state,
      action: PayloadAction<IRemittanceAPIProps.ILeadDocumentsListPayload>
    ) {
      state.documents.leadDocumentsList.loading = true;
      state.documents.leadDocumentsList.list = [];
      state.documents.leadDocumentsList.error = null;
      state.documents.recipientLeadDocs = [];
    },
    fetchLeadDocumentsListSucceeded(state, action: PayloadAction<any>) {
      state.documents.leadDocumentsList.loading = false;
      state.documents.leadDocumentsList.list = action.payload;
    },
    fetchLeadDocumentsListFailed(state, action: PayloadAction<any>) {
      state.documents.leadDocumentsList.loading = false;
      state.documents.leadDocumentsList.error = action.payload;
      state.documents.leadDocumentsList.list = [];
      state.documents.recipientLeadDocs = [];
    },

    // RECIPIENT LEAD DOCS SAVE
    setRecipientLeadDocsState(state, action) {
      state.documents.recipientLeadDocs = action.payload;
    },

    // RECIPIENT PREVIEW DOCS
    setRecipientPreviewDocsState(state, action) {
      state.documents.previewDocs = {
        ...state.documents.previewDocs,
        ...action.payload,
      };
    },

    // REVIEW OVERVIEW INPUTS
    setReviewDetailsInput(state, action: PayloadAction<any>) {
      state.review.reviewDetails = {
        ...state.review.reviewDetails,
        ...action.payload,
      };
    },

    // GET REMITTER SIGNED STATUS
    fetchRemitterSignedStatusRequested(state, action: PayloadAction<string>) {
      state.review.remitterSignedStatus.loading = true;
      state.review.remitterSignedStatus.error = null;
    },
    fetchRemitterSignedStatusSucceeded(
      state,
      action: PayloadAction<IReviewUIProps.RemitterSignedStatus>
    ) {
      state.review.remitterSignedStatus.loading = false;
      state.review.remitterSignedStatus.data = action.payload;
    },
    fetchRemitterSignedStatusFailed(state, action: PayloadAction<string>) {
      state.review.remitterSignedStatus.loading = false;
      state.review.remitterSignedStatus.error = action.payload;
    },

    // GET REMITTER SIGNED STATUS
    fetchGetModalContentRequested(state, action: PayloadAction<string>) {
      state.review.consentData.loading = true;
      state.review.consentData.error = null;
    },
    fetchGetModalContentSucceeded(state, action: PayloadAction<any>) {
      state.review.consentData.loading = false;
      state.review.consentData.data = action.payload;
    },
    fetchGetModalContentFailed(state, action: PayloadAction<string>) {
      state.review.consentData.loading = false;
      state.review.consentData.error = action.payload;
    },

    // SET VIEW A2 FORM STATE
    setViewA2FormState(state, action: PayloadAction<any>) {
      state.review.viewA2Form = {
        ...state.review.viewA2Form,
        ...action.payload,
      };
    },

    // SET DOWNLOAD A2 FORM STATE
    setDowanloadA2FormState(state, action: PayloadAction<any>) {
      state.review.downloadA2Form = {
        ...state.review.downloadA2Form,
        ...action.payload,
      };
    },

    // SET TRANSACTION DETAIL
    setTransactionDetail(state, action: PayloadAction<any>) {
      state.transactionDetail = {
        ...state.transactionDetail,
        ...action.payload,
      };
    },

    // GET NEFT/RTGS BANK DETAILS
    fetchGetNeftRtgsBankDetailsRequested(
      state,
      action: PayloadAction<IRemittanceAPIProps.INeftRtgsPayload>
    ) {
      state.payment.neftRtgsBankDetails.loading = true;
      state.payment.neftRtgsBankDetails.error = null;
      state.payment.neftRtgsBankDetails.data = null;
    },
    fetchGetNeftRtgsBankDetailsSucceeded(state, action: PayloadAction<any>) {
      state.payment.neftRtgsBankDetails.loading = false;
      state.payment.neftRtgsBankDetails.data = action.payload;
    },
    fetchGetNeftRtgsBankDetailsFailed(state, action: PayloadAction<string>) {
      state.payment.neftRtgsBankDetails.loading = false;
      state.payment.neftRtgsBankDetails.error = action.payload;
    },

    // SHARE NEFT/RTGS ACCOUNT DETAILS
    setShareNeftRtgsAccount(state, action: PayloadAction<any>) {
      state.payment.shareNeftRtgsAccount = {
        ...state.payment.shareNeftRtgsAccount,
        ...action.payload,
      };
    },

    // NET BANKING LIST
    fetchNetBankingListRequested(state) {
      state.payment.netBankingList.loading = true;
      state.payment.netBankingList.error = null;
      state.payment.netBankingList.list = [];
    },
    fetchNetBankingListSucceeded(state, action: PayloadAction<any>) {
      state.payment.netBankingList.loading = false;
      state.payment.netBankingList.list = action.payload;
    },
    fetchNetBankingListFailed(state, action: PayloadAction<string>) {
      state.payment.netBankingList.loading = false;
      state.payment.netBankingList.error = action.payload;
      state.payment.netBankingList.list = [];
    },

    // SET SEND PAYMENT LINK INPUT
    setSendPaymentLinkInput(state, action: PayloadAction<any>) {
      state.payment.sendPaymentLinkInput = {
        ...state.payment.sendPaymentLinkInput,
        ...action.payload,
      };
    },

    // SET RECIPIENT STEP NUMBER
    setRecipientStepNumber(state, action: PayloadAction<number>) {
      state.addrecipient.recipientStepNumber = action.payload;
    },

    // RECIPIENT CATEGORY LIST
    fetchRecipientCategoryListRequested(
      state,
      action: PayloadAction<IRemittanceAPIProps.IRecipientCategoryListParams>
    ) {
      state.addrecipient.recipientCategoryList.loading = true;
      state.addrecipient.recipientCategoryList.error = null;
      state.addrecipient.recipientCategoryList.list = [];
    },
    fetchRecipientCategoryListSucceeded(state, action: PayloadAction<any>) {
      state.addrecipient.recipientCategoryList.loading = false;
      state.addrecipient.recipientCategoryList.list = action.payload;
    },
    fetchRecipientCategoryListFailed(state, action: PayloadAction<string>) {
      state.addrecipient.recipientCategoryList.loading = false;
      state.addrecipient.recipientCategoryList.error = action.payload;
      state.addrecipient.recipientCategoryList.list = [];
    },

    // SET SEND PAYMENT LINK INPUT
    setAddRecipientInput(state, action: PayloadAction<any>) {
      state.addrecipient.recipientInput = {
        ...state.addrecipient.recipientInput,
        ...action.payload,
      };
    },

    // SET ADD RECIPIENT SUBMIT STATE
    setAddRecipientSubmit(state, action: PayloadAction<any>) {
      state.addrecipient.addRecipientSubmit = {
        ...state.addrecipient.addRecipientSubmit,
        ...action.payload,
      };
    },

    // RESET REMITTANCE STATE
    resetRemittanceState() {
      return initialState;
    },

    // RESET CALCULATOR STATE
    resetCalculatorState(state) {
      state.calculator = initialState.calculator;
    },

    // RESET REMITTER STATE
    resetRemitterState(state) {
      state.remitter = initialState.remitter;
    },

    // RESET REMITTER DOCUMENTS STATE
    resetRemitterDocumentsState(state) {
      state.remitter.remitterDocuments = initialState.remitter.remitterDocuments;
    },

    // RESET RECIPIENT STATE
    resetRecipientState(state) {
      state.recipient = initialState.recipient;
    },

    // RESET ADD RECIPIENT STATE
    resetAddRecipientState(state) {
      state.addrecipient = initialState.addrecipient;
    },

    // RESET DOCUMENTS STATE
    resetDocumentsState(state) {
      state.documents = initialState.documents;
    },

    // RESET REVIEW STATE
    resetReviewState(state) {
      state.review = initialState.review;
    },

    // RESET REVIEW STATE
    resetPaymentStateState(state) {
      state.payment = initialState.payment;
    },

    // RESET TRANSACTION DETAIL STATE
    resetTransactionDetailState(state) {
      state.transactionDetail = initialState.transactionDetail;
    },
  },
});

export const remittanceActions = remittanceSlice.actions;

export default remittanceSlice.reducer;
